import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './TopBanner.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import useLocale from '@/hooks/useLocale';
import {
  IconAiPencil2, IconAiFavorite, IconChevron, AlertV2,
  Skeleton,
} from 'gatorcomponents';
import GradientButton from '../GradientButton';
import { IconStars as IconStarsGradient } from '../Icons/IconStars';
import { IconCrown } from '../Icons/IconCrown';
import { IconFolder } from '../Icons/IconFolder';
import { IconMagicPen } from '../Icons/IconMagicPen';
import { useResize } from '@/hooks/useResize';
import { useStudio } from '@/hooks/studio';
import { CreditsView } from './components/CreditsView';
import { studioAnalytics } from '@/analytics/studio';
import IconShapedStars from '../Icons/IconShapedStars/IconShapedStars';
import { useHistory } from 'react-router';
import { CONTENT_TABS } from '../../studio.enum';

const TopBanner = ({
  isProPlan,
  credits,
  product,
  trialRemainingDays,
  handleShowModalGetPro,
  paidWithCreditCard,
  planPrice,
  loading,
  pickedTab,
  handleSetPickedTab,
  disableForMeOption,
}) => {
  const [bannerExpanded, setBannerExpanded] = useState(true);
  const [showCreditAnalysis, setShowCreditAnalysis] = useState(false);
  const [showTrialEndingAlert, setShowTrialEndingAlert] = useState(false);

  const windowRect = useResize();
  const history = useHistory();
  const { width } = windowRect;
  const mobile = width < 600;

  const { studio: studioLocale, routes: routesLocale } = useLocale();
  const { topBanner } = studioLocale;

  const { openStudio } = useStudio();

  const handleBannerExpanded = useCallback(() => {
    setBannerExpanded(prevState => !prevState);
    setShowTrialEndingAlert(false);
  }, []);

  const canShowAlert = useCallback(() => {
    if (trialRemainingDays === 3) {
      setShowTrialEndingAlert(true);
    }
  }, [trialRemainingDays]);

  useEffect(() => {
    canShowAlert();
  }, [canShowAlert]);

  const setBannerExpandedOnMobile = useCallback(() => {
    if (mobile) {
      setBannerExpanded(true);
    }
  }, [mobile]);

  useEffect(() => {
    setBannerExpandedOnMobile();
  }, [setBannerExpandedOnMobile]);

  const handleDisplayCreditsView = () => {
    setShowCreditAnalysis(!showCreditAnalysis);
    setShowTrialEndingAlert(false);
  };

  const handleProjectsClick = useCallback(() => {
    studioAnalytics.projectsClick();
    openStudio('documents_history');
  }, [openStudio]);

  const handleCreateAISiteClick = useCallback(() => {
    studioAnalytics.createSiteTopClick();
    history.push(`${routesLocale.sitesPage}${routesLocale.sitesCreate}`);
  }, [history, routesLocale]);

  const creditsViewProps = useCallback(() => ({
    isProPlan,
    setShowCreditAnalysis,
    handleShowModalGetPro,
    product,
    credits,
    creditsViewLocale: studioLocale.topBanner.creditAnalysis,
  }), [isProPlan, credits, handleShowModalGetPro, product, studioLocale]);

  if (loading) {
    return (
      <Skeleton width="100%" height="242px" />
    );
  }

  return (
    <Styles.Wrapper
      data-testid="top-banner-wrapper"
      isProPlan={isProPlan}
      bannerExpanded={bannerExpanded}
    >
      <Styles.Header bannerExpanded={bannerExpanded} isProPlan={isProPlan}>
        {isProPlan ? topBanner.pro : topBanner.free}
        <IconAiPencil2 color="highPure" size={mobile ? 'sm' : 'md'} />
      </Styles.Header>
      <Styles.CreditWrapper bannerExpanded={bannerExpanded}>
        <Styles.Credits data-testid="studio-credits" onClick={handleDisplayCreditsView}>
          <IconCrown />
          {credits.available || 0}
          {topBanner.credits}
        </Styles.Credits>

        {showCreditAnalysis && <CreditsView {...creditsViewProps()} />}

        {!showCreditAnalysis && showTrialEndingAlert && (
          <Styles.AlertWrapper>
            <AlertV2
              title={paidWithCreditCard ? topBanner.alertTitle(trialRemainingDays) : topBanner.alertTitleNoCard}
              description={paidWithCreditCard ? topBanner.alertDescription(planPrice) : topBanner.alertDescriptionNoCard}
              variant="info"
              hasIcon
              withoutBackground
              onClose={() => setShowTrialEndingAlert(false)}
              onPrimaryAction={() => handleShowModalGetPro && handleShowModalGetPro()}
              primaryButtonLabel={topBanner.ctaNoCard}
            />
          </Styles.AlertWrapper>
        )}
      </Styles.CreditWrapper>


      {!mobile && (
        <Styles.Title bannerExpanded={bannerExpanded}>
          {topBanner.title}
        </Styles.Title>
      )}

      {!isProPlan && (
        <Styles.UpgradeButtonWrapper>
          <GradientButton
            testId="upgrade-to-pro-button"
            label={topBanner.ButtonLabel}
            onClick={() => handleShowModalGetPro && handleShowModalGetPro()}
            iconLeft={<IconStarsGradient />}
            variant="secondary"
          />
        </Styles.UpgradeButtonWrapper>
      )}

      {isProPlan && (
        <Styles.ButtonGroup bannerExpanded={bannerExpanded}>
          <Styles.Button
            data-testid={`manage-${CONTENT_TABS.FOR_YOU}-button`}
            active={pickedTab === CONTENT_TABS.FOR_YOU}
            onClick={() => handleSetPickedTab(CONTENT_TABS.FOR_YOU)}
            disabled={disableForMeOption}
          >
            <IconAiFavorite size={mobile ? 'sm' : 'md'} />
            {topBanner.forYou}
          </Styles.Button>

          <Styles.Button
            data-testid={`manage-${CONTENT_TABS.DESIGN}-button`}
            active={pickedTab === CONTENT_TABS.DESIGN}
            onClick={() => handleSetPickedTab(CONTENT_TABS.DESIGN)}
          >
            <IconMagicPen size={mobile ? 'sm' : 'md'} />
            {topBanner.design}
          </Styles.Button>

          <Styles.Button
            data-testid={`manage-${CONTENT_TABS.PROJECTS}-button`}
            onClick={() => handleProjectsClick()}
          >
            <IconFolder size={mobile ? 'sm' : 'md'} />
            {topBanner.projects}
          </Styles.Button>

          {width < 960 && (
            <Styles.Button
              data-testid="build-ai-site-button"
              onClick={() => handleCreateAISiteClick()}
            >
              <IconShapedStars />
              {topBanner.createAiSite}
            </Styles.Button>
          )}
        </Styles.ButtonGroup>
      )}

      {!mobile && isProPlan && (
        <Styles.ExpandButton
          onClick={handleBannerExpanded}
          bannerExpanded={bannerExpanded}
        >
          <IconChevron direction="top" color="primaryBlue" />
        </Styles.ExpandButton>
      )}

    </Styles.Wrapper>
  );
};
export default withErrorBoundary(TopBanner, 'TopBanner');
